package com.islamicmedia.shared.platform

import kotlinx.serialization.Serializable

/**
 * Where one person originally came from — the acquisition half of [Funnel].
 *
 * FIRST-TOUCH, not last-touch. The value is captured on the very first page load / launch and then
 * repeated on every funnel event for that anon id, so "which channel produced this subscriber"
 * credits the thing that actually introduced them. Last-touch would keep overwriting it with
 * whatever link they clicked most recently, which is nearly always one of our own emails to
 * somebody we already had.
 *
 * WHERE THE VALUES COME FROM:
 *  - Web: `document.referrer` plus the `utm_*` / `gclid` / `fbclid` query params on the landing URL.
 *  - Android: the Google Play Install Referrer, which Play hands over as a utm-encoded query
 *    string — so it parses through exactly the same code path.
 *  - iOS: nothing. Apple exposes no free install-referrer equivalent (AdServices only covers Apple
 *    Search Ads), so iOS rows stay null. That is the correct steady state, not missing data.
 *
 * The vocabulary deliberately matches GA4's — `(direct)` / `(none)` / `organic` / `referral` /
 * `social` — so a breakdown here reads the same way as the Firebase reports for mobile.
 */
@Serializable
data class Attribution(
    /** `utm_source`, or the referring host when there was no campaign tag. `(direct)` if neither. */
    val source: String? = null,
    /** `utm_medium`, or a classification of the referrer: organic / social / referral. */
    val medium: String? = null,
    val campaign: String? = null,
    val term: String? = null,
    val content: String? = null,
    /**
     * HOST ONLY of the referring URL, never the full URL — a referring path and query string are
     * somebody else's data, and the host is the only part a report ever reads.
     */
    val referrerHost: String? = null,
    /** Paid-click id. At most one per visit, so the network is a sibling field rather than a column each. */
    val clickId: String? = null,
    /** Which network issued [clickId]: `gclid` or `fbclid`. */
    val clickIdType: String? = null,
    /** First path landed on (`/`, `/isitsafe/frozen`). PATH ONLY — the utm values are their own fields. */
    val landingPath: String? = null,
) {
    /**
     * True when this record carries no ACTIONABLE acquisition signal, so first-touch capture stays
     * open for a later visit that does.
     *
     * The subtlety: [from] never returns all-nulls — with nothing to go on it still reports
     * `(direct)`/`(none)`. Treating that as a real answer would let the first untagged visit lock
     * itself in and silently discard every campaign link the person clicked afterwards, which is
     * the opposite of useful. GA4 behaves the same way: direct never displaces a known source.
     *
     * An explicit `utm_medium` with no `utm_source` still counts as a signal — [medium] is only
     * ignorable when it is the derived `(none)`.
     */
    fun isEmpty(): Boolean =
        referrerHost == null && clickId == null &&
            campaign == null && term == null && content == null &&
            (source == null || source == DIRECT_SOURCE) &&
            (medium == null || medium == NONE_MEDIUM)

    companion object {
        /** Reported when there was genuinely nothing to go on. Distinct from a NULL source, which
         *  means attribution was never captured at all. */
        const val DIRECT_SOURCE = "(direct)"
        const val NONE_MEDIUM = "(none)"

        /** Our own hosts. A referrer from one of these is internal navigation, never an acquisition source. */
        private val OWN_HOSTS = setOf(
            "fitrahfilter.com", "www.fitrahfilter.com",
            "islamic-media-api.fly.dev", "fitrah-filter-dev.fly.dev",
            "localhost",
        )

        private val SEARCH_HOSTS = setOf(
            "google", "bing", "duckduckgo", "yahoo", "ecosia", "baidu", "yandex", "brave", "startpage",
        )

        private val SOCIAL_HOSTS = setOf(
            "facebook", "instagram", "twitter", "x", "t", "tiktok", "youtube", "reddit",
            "linkedin", "pinterest", "whatsapp", "telegram", "snapchat", "threads",
        )

        /**
         * Build a first-touch record from a landing URL's query string and its referrer.
         *
         * An explicit `utm_source` always wins — that is someone deliberately tagging a link, and
         * guessing from the referrer on top of it would only overwrite a known answer with a
         * derived one. Referrer classification is the fallback for untagged traffic.
         */
        fun from(rawQuery: String?, referrerUrl: String?, landingPath: String?): Attribution {
            val q = parseQuery(rawQuery)
            // `ref` is a referrer forwarded through a hop that lost the real one — the landing
            // page hands the app its own document.referrer this way, because the app boots inside
            // a no-referrer iframe and would otherwise see every referral as direct traffic.
            val refHost = hostOf(referrerUrl?.takeIf { it.isNotBlank() } ?: q["ref"])
                ?.takeIf { it !in OWN_HOSTS }

            val gclid = q["gclid"]
            val fbclid = q["fbclid"]
            val clickId = gclid ?: fbclid
            val clickIdType = when {
                gclid != null -> "gclid"
                fbclid != null -> "fbclid"
                else -> null
            }

            val utmSource = q["utm_source"]
            val utmMedium = q["utm_medium"]

            // Derived source/medium, used only where the link itself said nothing.
            val derivedSource: String?
            val derivedMedium: String?
            when {
                // Only Google Ads emits gclid, so it IS a reliable paid signal.
                gclid != null -> { derivedSource = "google"; derivedMedium = "cpc" }
                // fbclid deliberately does NOT imply paid, and is checked AFTER the referrer: Meta
                // appends it to every outbound click including organic posts and bio links, so
                // treating it as paid would invent ad spend we never made. The referrer is the more
                // informative signal when we have it (l.instagram.com beats a generic "facebook");
                // fbclid only fills in when the referrer was stripped.
                refHost != null -> {
                    derivedSource = refHost
                    derivedMedium = when (registrableName(refHost)) {
                        in SEARCH_HOSTS -> "organic"
                        in SOCIAL_HOSTS -> "social"
                        else -> "referral"
                    }
                }
                fbclid != null -> { derivedSource = "facebook"; derivedMedium = "social" }
                // No tag, no referrer: typed the URL, a bookmark, or an app the browser can't see.
                else -> { derivedSource = DIRECT_SOURCE; derivedMedium = NONE_MEDIUM }
            }

            return Attribution(
                source = utmSource ?: derivedSource,
                medium = utmMedium ?: derivedMedium,
                campaign = q["utm_campaign"],
                term = q["utm_term"],
                content = q["utm_content"],
                referrerHost = refHost,
                clickId = clickId,
                clickIdType = clickIdType,
                landingPath = landingPath?.takeIf { it.isNotBlank() },
            )
        }

        /**
         * Parse the Play Install Referrer string, which Play hands over already shaped as a query
         * string (`utm_source=google-play&utm_medium=organic`). There is no referrer URL and no
         * landing path on an install, so those stay null.
         */
        fun fromInstallReferrer(raw: String): Attribution =
            from(rawQuery = raw, referrerUrl = null, landingPath = null).let {
                when {
                    // Play answered, but the store link carried no tags at all. "Came via the
                    // store" is still more truthful than the `(direct)` that from() falls back to
                    // for an untagged WEB visit — an install is never direct in the same sense.
                    it.isEmpty() -> it.copy(source = "google-play", medium = "organic")
                    // A tagged link that named a medium but no source. Only the DERIVED source
                    // needs replacing; overwriting the explicit medium too would throw away the one
                    // piece of real data the link actually carried.
                    it.source == DIRECT_SOURCE -> it.copy(source = "google-play")
                    else -> it
                }
            }

        /** `a=1&b=2` → map. Tolerates a leading `?`, blank values, and repeated keys (first wins). */
        private fun parseQuery(raw: String?): Map<String, String> {
            if (raw.isNullOrBlank()) return emptyMap()
            val out = mutableMapOf<String, String>()
            raw.removePrefix("?").split("&").forEach { pair ->
                if (pair.isBlank()) return@forEach
                val i = pair.indexOf('=')
                if (i <= 0) return@forEach
                val k = percentDecode(pair.substring(0, i)).lowercase()
                val v = percentDecode(pair.substring(i + 1)).trim()
                if (v.isNotEmpty() && !out.containsKey(k)) out[k] = v
            }
            return out
        }

        /**
         * Host of a URL, lowercased, without scheme, userinfo or port. Returns null for a blank or
         * unparseable referrer. Hand-rolled because commonMain has no URL type and the shapes we
         * see here are ordinary absolute http(s) URLs.
         */
        private fun hostOf(url: String?): String? {
            if (url.isNullOrBlank()) return null
            var s = url.trim()
            val scheme = s.indexOf("://")
            if (scheme >= 0) s = s.substring(scheme + 3)
            s = s.substringBefore('/').substringBefore('?').substringBefore('#')
            s = s.substringAfterLast('@')      // strip user:pass@
            s = s.substringBefore(':')          // strip :port
            return s.lowercase().takeIf { it.isNotBlank() }
        }

        /**
         * The name a host is recognised BY, for classification: `www.google.com` → `google`,
         * `l.instagram.com` → `instagram`, `t.co` → `t`. Takes the label before the public suffix,
         * treating a two-part suffix (`co.uk`, `com.au`) as one — enough to sort search from social
         * from everything else, which is all this is used for.
         */
        private fun registrableName(host: String): String {
            val parts = host.split(".").filter { it.isNotBlank() }
            if (parts.size < 2) return host
            val last = parts[parts.size - 1]
            val secondLast = parts[parts.size - 2]
            val twoPartSuffix = last.length == 2 && secondLast.length <= 3 && parts.size >= 3
            return if (twoPartSuffix) parts[parts.size - 3] else secondLast
        }

        /**
         * Decode `%XX` escapes and `+` as space.
         *
         * Consecutive escapes are accumulated as BYTES and decoded as UTF-8 in one go, rather than
         * turned into a char each: a non-ASCII campaign name arrives as a multi-byte sequence
         * (`%D8%A7` is one Arabic letter, not two characters), and decoding byte-by-byte would
         * turn it into mojibake. Malformed escapes are left as literal text.
         */
        private fun percentDecode(s: String): String {
            if ('%' !in s && '+' !in s) return s
            val sb = StringBuilder(s.length)
            val bytes = mutableListOf<Byte>()
            fun flush() {
                if (bytes.isNotEmpty()) {
                    sb.append(bytes.toByteArray().decodeToString())
                    bytes.clear()
                }
            }
            var i = 0
            while (i < s.length) {
                val c = s[i]
                when {
                    c == '%' && i + 2 < s.length && s.substring(i + 1, i + 3).toIntOrNull(16) != null -> {
                        bytes.add(s.substring(i + 1, i + 3).toInt(16).toByte())
                        i += 3
                    }
                    c == '+' -> { flush(); sb.append(' '); i++ }
                    else -> { flush(); sb.append(c); i++ }
                }
            }
            flush()
            return sb.toString()
        }
    }
}
